import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, TrendingUp, CheckCircle, DollarSign, Calendar, Award
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi",
  construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial",
};

export default function DealHistoryTab({ headers }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/investor/deal-history`, { headers })
      .then(r => setDeals(r.data.deals || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="deal-history-loading">
      <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />
    </div>
  );

  const totalVolume = deals.reduce((s, d) => s + (d.loan_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="deal-history-tab">
      {/* Hero Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-[#e8e0d0] bg-gradient-to-br from-emerald-50 to-white p-4 text-center">
          <Award className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
          <div className="text-2xl font-black text-emerald-600">{deals.length}</div>
          <div className="text-[10px] text-[#999] uppercase tracking-wider">Deals Funded</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="rounded-xl border border-[#e8e0d0] bg-gradient-to-br from-[#faf7f2] to-white p-4 text-center">
          <DollarSign className="h-6 w-6 text-[#b89d5a] mx-auto mb-2" />
          <div className="text-2xl font-black text-[#b89d5a]">${(totalVolume / 1e6).toFixed(2)}M</div>
          <div className="text-[10px] text-[#999] uppercase tracking-wider">Total Volume</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-xl border border-[#e8e0d0] bg-gradient-to-br from-blue-50 to-white p-4 text-center">
          <TrendingUp className="h-6 w-6 text-blue-500 mx-auto mb-2" />
          <div className="text-2xl font-black text-blue-600">100%</div>
          <div className="text-[10px] text-[#999] uppercase tracking-wider">Success Rate</div>
        </motion.div>
      </div>

      {deals.length === 0 ? (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-12 text-center">
          <Calendar className="h-12 w-12 text-[#ddd] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">Building Track Record</h3>
          <p className="text-sm text-[#666] max-w-md mx-auto">
            As deals close and investors receive returns, completed deals will appear here. This is where the platform's track record becomes visible.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {deals.map((deal, idx) => (
            <motion.div key={deal.opportunity_id || deal.deal_number || idx}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
              className="rounded-xl border border-[#e8e0d0] bg-white p-4 hover:bg-[#faf7f2]/60 transition-colors"
              data-testid={`history-deal-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#0A0A0A]">
                        {LOAN_LABELS[deal.loan_type] || deal.loan_type || "Real Estate"}
                      </span>
                      {deal.deal_number && (
                        <span className="text-[10px] font-mono text-emerald-600">{deal.deal_number}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#999]">
                      {deal.state && `${deal.state} `}
                      {deal.created_at && new Date(deal.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <div className="text-sm font-bold text-[#0A0A0A]">${(deal.loan_amount / 1e3).toFixed(0)}K</div>
                    <div className="text-[9px] text-[#999] uppercase">Loan Amount</div>
                  </div>
                  {deal.ltv && (
                    <div>
                      <div className={`text-sm font-bold ${deal.ltv <= 70 ? "text-emerald-600" : "text-amber-600"}`}>{deal.ltv}%</div>
                      <div className="text-[9px] text-[#999] uppercase">LTV</div>
                    </div>
                  )}
                  {deal.projected_yield && (
                    <div>
                      <div className="text-sm font-bold text-[#b89d5a]">{deal.projected_yield}%</div>
                      <div className="text-[9px] text-[#999] uppercase">Yield</div>
                    </div>
                  )}
                  {deal.projected_term_months && (
                    <div>
                      <div className="text-sm font-bold text-[#555]">{deal.projected_term_months}mo</div>
                      <div className="text-[9px] text-[#999] uppercase">Term</div>
                    </div>
                  )}
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-green-50 text-green-600 border border-green-200">
                    {deal.status === "funded" ? "Funded" : "Closed"}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
